import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useMotionValue, useSpring } from 'framer-motion';
import { EASE_LUSION } from './easing';

/**
 * Ink-dot cursor with a trailing ring, and the site's hover affordance.
 *
 * Two levels, on purpose. Anything clickable at all grows the ring a little —
 * that is the quiet "this is a target" signal, and it costs nothing to read.
 * Targets whose action is not obvious from looking at them ask for a word as
 * well, with `data-cursor="VIEW"`, and the ring becomes a small pill carrying
 * it. Buttons that already say what they do ("CV", "Menu") deliberately do not
 * take a label — a pill repeating the word underneath it is noise.
 *
 * The pill is small on purpose. It used to be 64px tall, which read as a
 * component following the pointer around rather than a hint attached to it.
 */
const CursorV2: React.FC = () => {
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const ringX = useSpring(x, { stiffness: 260, damping: 24, mass: 0.5 });
  const ringY = useSpring(y, { stiffness: 260, damping: 24, mass: 0.5 });
  const [label, setLabel] = useState<string | null>(null);
  const [hoveringLink, setHoveringLink] = useState(false);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      x.set(e.clientX);
      y.set(e.clientY);
    };
    const onOver = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      const labelled = t.closest('[data-cursor]') as HTMLElement | null;
      setLabel(labelled?.dataset.cursor || null);
      setHoveringLink(!!t.closest('a, button'));
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mouseover', onOver, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseover', onOver);
    };
  }, [x, y]);

  if (typeof window !== 'undefined' && window.matchMedia && !window.matchMedia('(pointer: fine)').matches) {
    return null;
  }

  return (
    <>
      {/* Dot — hard-locked to the pointer */}
      <motion.div
        className="fixed top-0 left-0 z-[9999] pointer-events-none mix-blend-difference"
        style={{ x, y }}
      >
        <motion.div
          className="w-2 h-2 -ml-1 -mt-1 rounded-full bg-white"
          animate={{ scale: label ? 0 : hoveringLink ? 0.5 : 1 }}
          transition={{ duration: 0.2 }}
        />
      </motion.div>

      {/* Ring / label pill — trails with spring lag */}
      <motion.div
        className="fixed top-0 left-0 z-[9998] pointer-events-none"
        style={{ x: ringX, y: ringY }}
      >
        <motion.div
          // centring must be framer-owned (-50% here, not Tailwind classes):
          // motion manages the transform property, so class-based translates
          // silently lose and the ring hangs off-centre from the point
          style={{ x: '-50%', y: '-50%' }}
          // The ring is what guarantees contrast. The pill is electric, and so
          // is the email button's hover state, so on that one target it was
          // blue on blue and the pill's edge vanished. A hairline of white
          // separates it from any surface its own colour, and is invisible
          // against the light sections where the fill already carries itself.
          className={`flex items-center justify-center overflow-hidden rounded-full ${
            label
              // one arbitrary shadow rather than ring-*: framer owns this
              // element's style and Tailwind's ring composes through custom
              // properties that never resolved here, so the hairline silently
              // did nothing. A spread shadow is unconditional.
              ? 'bg-electric text-white px-3 shadow-[0_0_0_1.5px_rgba(255,255,255,0.8),0_2px_12px_rgba(11,12,16,0.25)]'
              : 'border border-ink/30 mix-blend-difference'
          }`}
          animate={{
            // width follows the text so long labels sit in a capsule instead
            // of overflowing a fixed circle
            width: label ? 'auto' : hoveringLink ? 38 : 26,
            height: label ? 30 : hoveringLink ? 38 : 26,
          }}
          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        >
          {/* the label rolls, rather than appearing: moving between two
              labelled targets swaps the word the same way the contact pills
              and the marquee headings swap theirs. Keyed on the text, so
              framer treats a different word as a different element. */}
          <AnimatePresence mode="wait" initial={false}>
            {label && (
              <motion.span
                key={label}
                className="block font-grotesk text-[9px] font-semibold uppercase tracking-[0.16em] whitespace-nowrap text-center pl-[0.16em]"
                // pl offsets the trailing letter-spacing after the last glyph,
                // which otherwise drags the text off optical centre
                initial={{ y: '115%', opacity: 0 }}
                animate={{ y: '0%', opacity: 1 }}
                exit={{ y: '-115%', opacity: 0 }}
                transition={{ duration: 0.22, ease: EASE_LUSION }}
              >
                {label}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </>
  );
};

export default CursorV2;
